/**
 * 模拟核心 —— 《传灯》的确定性游戏机制
 *
 * 铁律：
 * 1. simulate() 是纯函数：同 setup + 同 actions 必得同 result。
 * 2. 禁止读取 Date.now / Math.random / 任何全局状态。
 * 3. 客户端用它跑游戏，服务端用它校验结算 —— 改这里就是同时改两端。
 *
 * 数值旋钮全部收敛在 `constants.ts`（DEFAULT_CONSTANTS），关卡可通过
 * `MatchSetup.constants` 覆盖；随机事件来自 `setup.eventPool`（数据驱动）。
 * "能不能做某个操作"的判定统一放在 `query.ts`，UI 与本题共用同一份规则。
 */

import {
  DEFAULT_EVENT_CHANCE,
  ENLIGHTENMENT_MAX,
  IGNITE_COST,
  MIST_MAX,
  MIST_SUPPRESS_STEP,
  resolveConstants,
  SELF_GROWTH_PER_TURN,
} from "./constants.js";
import { collectEffects, spreadCost, type SkillEffects } from "./effects.js";
import { DEFAULT_EVENT_POOL, drawEvent } from "./events/pool.js";
import {
  averageEnlightenment,
  reachableRegionIds,
  routeBlockReasonIgnoringEnergy,
} from "./query.js";
import { deriveSeed, fnv1a, mulberry32, pick, type Rng } from "./rng.js";
import { SPREAD_RADIUS_UNIT } from "./skilltree/life.js";
import type {
  Action,
  EventEffect,
  MatchSetup,
  RandomEventDef,
  RegionState,
  SimConstants,
  SimEvent,
  SimResult,
  SimState,
} from "./types.js";

export function simulate(setup: MatchSetup, actions: Action[]): SimResult {
  const constants = resolveConstants(setup.constants);
  const state = initState(setup, constants);
  const rng = mulberry32(deriveSeed(setup.eventSeed, "events"));
  const events: SimEvent[] = [];
  const effects = collectEffects(setup.skillTree, state.unlockedNodeIds);
  const eventPool = setup.eventPool ?? DEFAULT_EVENT_POOL;
  const eventChance = setup.eventChance ?? DEFAULT_EVENT_CHANCE;

  refreshRevealedRoutes(setup, state, effects);

  let actionIndex = 0;
  while (!state.finished && actionIndex < actions.length) {
    const action = actions[actionIndex++]!;
    applyAction(setup, state, effects, constants, action, events);

    if (action.t === "endTurn") {
      endTurn(setup, state, effects, constants, rng, events, eventPool, eventChance);
      checkVictory(setup, state, events);
    }

    refreshLit(state, constants);
    refreshRevealedRoutes(setup, state, effects);
  }

  const score = computeScore(state, constants);
  const checksum = fnv1a(JSON.stringify(state));
  return { finalState: state, events, score, checksum };
}

// ---------- 初始化 ----------

function initState(setup: MatchSetup, constants: SimConstants): SimState {
  const mapRng = mulberry32(deriveSeed(setup.mapSeed, "init"));
  const regions: Record<string, RegionState> = {};
  for (const region of setup.map.regions) {
    const jitter = Math.floor(mapRng() * 6); // 每局初始启蒙度有微小随机差异
    const enlightenment = clamp(
      region.initialEnlightenment + jitter,
      0,
      constants.litThreshold - 1,
    );
    regions[region.id] = {
      regionId: region.id,
      enlightenment,
      lit: false,
      ignited: false,
    };
  }

  const state: SimState = {
    turn: 1,
    energy: constants.startEnergy,
    wisdom: 0,
    mist: 0,
    regions,
    unlockedNodeIds: [],
    revealedRouteIds: [],
    closedRouteIds: [],
    unlockedCardIds: [],
    finished: false,
    outcome: null,
  };

  // 起始火种：开局即已点燃，并带来一份初始启蒙度
  const start = setup.startRegionId ? regions[setup.startRegionId] : undefined;
  if (start) {
    start.ignited = true;
    start.enlightenment = clamp(start.enlightenment + constants.startBonus, 0, ENLIGHTENMENT_MAX);
  }

  refreshLit(state, constants);
  return state;
}

// ---------- 行动处理 ----------

function applyAction(
  setup: MatchSetup,
  state: SimState,
  effects: SkillEffects,
  constants: SimConstants,
  action: Action,
  events: SimEvent[],
): void {
  switch (action.t) {
    case "ignite": {
      const region = state.regions[action.regionId];
      if (!region || region.ignited || state.energy < IGNITE_COST) return;
      // 只能在已有火种可抵达的相邻区域开辟新营地 —— 地图拓扑因此有意义
      if (!reachableRegionIds(setup, state).includes(action.regionId)) return;
      state.energy -= IGNITE_COST;
      region.ignited = true;
      applyEnlightenment(state, action.regionId, constants.startBonus, constants);
      events.push({
        turn: state.turn,
        kind: "ignite",
        message: `火种在 ${regionName(setup, action.regionId)} 点燃`,
        payload: { regionId: action.regionId },
      });
      break;
    }
    case "spread": {
      const route = setup.map.routes.find((r) => r.id === action.routeId);
      if (!route) return;
      if (routeBlockReasonIgnoringEnergy(setup, state, route)) return;
      const from = state.regions[route.from];
      const to = state.regions[route.to];
      if (!from?.ignited || !to) return;
      const cost = spreadCost(route.cost, effects);
      if (state.energy < cost) return;
      state.energy -= cost;

      const gain = Math.round(constants.spreadBase * effects.spreadSpeedMul);
      applyEnlightenment(state, route.to, gain, constants);
      state.wisdom += constants.spreadWisdom;

      // 传播波及：额外点亮目标区域周边的若干区域（技能节点「观察与记录」「举灯传火」）
      const spilled = spillToNeighbours(setup, state, route.to, route.from, effects, constants);

      events.push({
        turn: state.turn,
        kind: "spread",
        message: `火种沿 ${route.id} 传播，${regionName(setup, route.to)} 启蒙度 +${gain}`,
        payload: { routeId: route.id, regionId: route.to, gain, spilled },
      });
      break;
    }
    case "upgrade": {
      const node = setup.skillTree.find((n) => n.id === action.nodeId);
      if (!node || state.unlockedNodeIds.includes(node.id)) return;
      if (node.parentId && !state.unlockedNodeIds.includes(node.parentId)) return;
      if (state.wisdom < node.cost) return;
      state.wisdom -= node.cost;
      state.unlockedNodeIds.push(node.id);
      if (node.cardId && !state.unlockedCardIds.includes(node.cardId)) {
        state.unlockedCardIds.push(node.cardId);
      }
      events.push({
        turn: state.turn,
        kind: "upgrade",
        message: `解锁知识节点「${node.name}」`,
        payload: { cardId: node.cardId, nodeId: node.id },
      });
      Object.assign(effects, collectEffects(setup.skillTree, state.unlockedNodeIds));
      break;
    }
    case "answer": {
      // 答对大幅增益、答错小额保底 —— 答题是增益不是门槛（GDD 2.4）
      state.wisdom += action.correct ? constants.answerWisdom : constants.answerHalfWisdom;
      if (action.correct && action.cardId && !state.unlockedCardIds.includes(action.cardId)) {
        state.unlockedCardIds.push(action.cardId);
      }
      events.push({
        turn: state.turn,
        kind: "answer",
        message: action.correct ? "考验通过，智慧之光更盛" : "虽有困惑，仍有所得",
        payload: {
          questionId: action.questionId,
          correct: action.correct,
          cardId: action.cardId,
        },
      });
      break;
    }
    case "endTurn":
      break;
  }
}

// ---------- 回合推进 ----------

function endTurn(
  setup: MatchSetup,
  state: SimState,
  effects: SkillEffects,
  constants: SimConstants,
  rng: Rng,
  events: SimEvent[],
  eventPool: readonly RandomEventDef[],
  eventChance: number,
): void {
  // 1. 上一回合被封锁的商路自动恢复通行
  state.closedRouteIds = [];
  refreshLit(state, constants);

  // 2. 火种自燃：已点亮的区域缓慢增长
  for (const region of Object.values(state.regions)) {
    if (region.lit) {
      region.enlightenment = clamp(
        region.enlightenment + SELF_GROWTH_PER_TURN,
        0,
        ENLIGHTENMENT_MAX,
      );
    }
  }

  // 3. 迷雾蔓延（镜像原版"解药研发"的压力）
  const mistGain = Math.max(0, setup.mistGrowth - effects.mistResist);
  state.mist = clamp(state.mist + mistGain, 0, MIST_MAX);

  // 3.5 迷雾压制：浓度每满一个台阶，未点亮区域每回合被侵蚀 1 点
  const suppress = Math.floor(state.mist / MIST_SUPPRESS_STEP);
  if (suppress > 0) {
    for (const region of Object.values(state.regions)) {
      if (!region.lit) {
        region.enlightenment = clamp(region.enlightenment - suppress, 0, ENLIGHTENMENT_MAX);
      }
    }
  }

  // 4. 随机事件：从关卡事件池按权重确定性抽取
  if (eventPool.length > 0 && rng() < eventChance) {
    const def = drawEvent(rng, eventPool, { mist: state.mist, turn: state.turn });
    if (def) applyEventEffects(setup, state, effects, constants, def, rng, events);
  }

  // 5. 能量回复
  state.energy = Math.min(
    constants.energyCap + effects.maxEnergyBonus,
    state.energy + constants.energyPerTurn + effects.energyRegenBonus,
  );
  state.turn += 1;
  refreshLit(state, constants);

  if (state.turn > constants.turnLimit) {
    state.finished = true;
    state.outcome = "lose";
    events.push({ turn: state.turn, kind: "finish", message: "迷雾吞没了大地（回合耗尽）" });
  }
}

function checkVictory(setup: MatchSetup, state: SimState, events: SimEvent[]): void {
  if (state.finished) return;
  if (averageEnlightenment(state) >= setup.goalEnlightenment) {
    state.finished = true;
    state.outcome = "win";
    events.push({ turn: state.turn, kind: "finish", message: "灯火相传，迷雾退散！" });
  }
}

// ---------- 随机事件效果 ----------

function applyEventEffects(
  setup: MatchSetup,
  state: SimState,
  effects: SkillEffects,
  constants: SimConstants,
  def: RandomEventDef,
  rng: Rng,
  events: SimEvent[],
): void {
  for (const effect of def.effects) {
    switch (effect.kind) {
      case "mist":
        state.mist = clamp(state.mist + effect.value, 0, MIST_MAX);
        break;
      case "energy":
        state.energy = clamp(
          state.energy + effect.value,
          0,
          constants.energyCap + effects.maxEnergyBonus,
        );
        break;
      case "wisdom":
        state.wisdom = Math.max(0, state.wisdom + effect.value);
        break;
      case "enlightenment": {
        const targetId = resolveEffectTarget(setup, state, effect, rng);
        if (targetId) applyEnlightenment(state, targetId, effect.value, constants);
        break;
      }
      case "closeRoute": {
        if (effect.routeId) {
          if (isStructurallyUsable(setup, state, effect.routeId)) {
            state.closedRouteIds.push(effect.routeId);
          }
          break;
        }
        const candidates = setup.map.routes
          .filter((r) => isStructurallyUsable(setup, state, r.id))
          .map((r) => r.id);
        if (candidates.length > 0) state.closedRouteIds.push(pick(rng, candidates));
        break;
      }
    }
  }

  events.push({
    turn: state.turn,
    kind: def.kind,
    message: def.message,
    payload: { eventId: def.id, title: def.title },
  });
}

/** 解析事件效果的作用区域（纯函数，随机部分由 eventSeed 派生） */
function resolveEffectTarget(
  setup: MatchSetup,
  state: SimState,
  effect: EventEffect,
  rng: Rng,
): string | null {
  if (effect.regionId) return effect.regionId;

  const all = Object.values(state.regions);
  let pool: RegionState[];
  switch (effect.target) {
    case "randomDark":
      pool = all.filter((r) => !r.lit && r.enlightenment > 0);
      break;
    case "randomLit":
      pool = all.filter((r) => r.lit);
      break;
    case "randomAny":
      pool = all;
      break;
    default:
      // target 为 none / 未指定时，效果不针对具体区域
      return null;
  }
  if (pool.length === 0) return null;
  return pick(rng, pool).regionId;
}

// ---------- 结算 ----------

function computeScore(state: SimState, constants: SimConstants): number {
  if (state.outcome !== "win") return 0;
  const lit = Object.values(state.regions).filter((r) => r.lit).length;
  const avg = averageEnlightenment(state);
  // 越早赢、点亮越多、迷雾越低分越高
  return Math.round(lit * 100 + avg * 10 + (constants.turnLimit - state.turn) * 5 - state.mist);
}

/** 星级评定（1 星通关 / 2 星省回合 / 3 星全点亮，见关卡设计表第 7 节） */
export function computeStars(state: SimState, constants: SimConstants): number {
  if (state.outcome !== "win") return 0;
  const regions = Object.values(state.regions);
  const allLit = regions.length > 0 && regions.every((r) => r.lit);
  const turnsLeft = constants.turnLimit - state.turn;
  if (allLit) return 3;
  if (turnsLeft >= constants.turnLimit * 0.2) return 2;
  return 1;
}

// ---------- 状态维护工具 ----------

/** 刷新"是否点亮"派生标记（启蒙度跨过阈值即点亮） */
function refreshLit(state: SimState, constants: SimConstants): void {
  for (const region of Object.values(state.regions)) {
    region.lit = region.enlightenment >= constants.litThreshold;
  }
}

/** 按 revealRoute 效果把隐藏海路逐条显现（顺序 = 地图声明顺序，保证确定性） */
function refreshRevealedRoutes(
  setup: MatchSetup,
  state: SimState,
  effects: SkillEffects,
): void {
  const hidden = setup.map.routes.filter((r) => r.hiddenByDefault).map((r) => r.id);
  const count = Math.min(effects.revealRouteCount, hidden.length);
  state.revealedRouteIds = hidden.slice(0, count);
}

function applyEnlightenment(
  state: SimState,
  regionId: string,
  delta: number,
  constants: SimConstants,
): void {
  const region = state.regions[regionId];
  if (!region) return;
  region.enlightenment = clamp(region.enlightenment + delta, 0, ENLIGHTENMENT_MAX);
  // 启蒙度过半即成为可继续向外传播的营地
  region.ignited ||= region.enlightenment >= constants.litThreshold / 2;
}

/** 传播波及：对目标区域的相邻区域追加一份微量启蒙度 */
function spillToNeighbours(
  setup: MatchSetup,
  state: SimState,
  centreId: string,
  excludeId: string,
  effects: SkillEffects,
  constants: SimConstants,
): string[] {
  if (effects.spreadRadiusCount <= 0) return [];
  const neighbours: string[] = [];
  for (const route of setup.map.routes) {
    if (routeBlockReasonIgnoringEnergy(setup, state, route)) continue;
    if (route.from === centreId && route.to !== excludeId) neighbours.push(route.to);
    else if (route.to === centreId && route.from !== excludeId) neighbours.push(route.from);
  }
  const touched = neighbours.slice(0, effects.spreadRadiusCount);
  for (const id of touched) {
    applyEnlightenment(state, id, SPREAD_RADIUS_UNIT, constants);
  }
  return touched;
}

/** 路线在"结构上"是否可用（与 query.ts 共用同一规则） */
function isStructurallyUsable(setup: MatchSetup, state: SimState, routeId: string): boolean {
  const route = setup.map.routes.find((r) => r.id === routeId);
  if (!route) return false;
  return routeBlockReasonIgnoringEnergy(setup, state, route) === null;
}

function regionName(setup: MatchSetup, regionId: string): string {
  return setup.map.regions.find((r) => r.id === regionId)?.name ?? regionId;
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}
